import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import useLoadedUser from "../../hooks/auth/useLoadedUser";
import { trackAdminEvent } from "../../../services/AdminTelemetryService";
import { useClickTracking } from "./hooks/useClickTracking";
import { useRageClick } from "./hooks/useRageClick";
import { useEngagement } from "./hooks/useEngagement";
import { useWebVitals } from "./hooks/useWebVitals";

function normalizeUnhandledReason(reason: any) {
	if (typeof reason === "string") return reason;
	if (reason?.message) return reason.message;
	if (reason?.stack) return reason.stack;
	try {
		return JSON.stringify(reason);
	} catch (_) {
		return "Unhandled promise rejection";
	}
}

export default function AdminTelemetry() {
	useClickTracking();
	useRageClick();
	useEngagement();
	useWebVitals();

	const location = useLocation();
	const { user } = useLoadedUser();
	const lastTrackedRouteRef = useRef("");

	useEffect(() => {
		const route = `${location.pathname}${location.search}${location.hash}`;
		if (lastTrackedRouteRef.current === route) return;
		lastTrackedRouteRef.current = route;

		void trackAdminEvent({
			eventType: "page_view",
			level: "info",
			action: "page_view",
			route,
			metadata: {
				pageTitle: typeof document !== "undefined" ? document.title : null,
				userRole: user?.role || null,
			},
		});
	}, [location.hash, location.pathname, location.search, user?.role]);

	useEffect(() => {
		const onWindowError = (event: ErrorEvent) => {
			void trackAdminEvent({
				eventType: "runtime_error",
				level: "error",
				action: "window_error",
				message: event.message || "Unhandled window error",
				metadata: {
					filename: event.filename || null,
					lineNumber: event.lineno || null,
					columnNumber: event.colno || null,
					stack: event.error?.stack || null,
				},
			});
		};

		const onUnhandledRejection = (event: PromiseRejectionEvent) => {
			void trackAdminEvent({
				eventType: "runtime_error",
				level: "error",
				action: "unhandled_promise_rejection",
				message: normalizeUnhandledReason(event.reason),
				metadata: {
					stack: event.reason?.stack || null,
				},
			});
		};

		window.addEventListener("error", onWindowError);
		window.addEventListener("unhandledrejection", onUnhandledRejection);

		return () => {
			window.removeEventListener("error", onWindowError);
			window.removeEventListener("unhandledrejection", onUnhandledRejection);
		};
	}, []);

	return null;
}
